import { IUser, UserViewModel } from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { getDisplayName } from '../../../../models/NameUtils';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../../../components-v2/Select';
import { UserSelectModal } from '../../UserSelectModal';
import { styleSheet } from './styles';

export enum ActionItemsFilterValue {
	All = 'All',
	CurrentUser = 'CurrentUser',
	OwnedBy = 'OwnedBy',
}

interface IProps {
	onFilterValueChanged?(value: ActionItemsFilterValue, user?: IUser): void;
	selectedFilterValue?: ActionItemsFilterValue;
}

interface IState {
	selectedEmployee?: IUser;
	selectedFilterValue?: ActionItemsFilterValue;
	showSelectEmployeeModal?: boolean;
}

export class ActionItemsFilter extends React.Component<IProps, IState> {
	public readonly state: IState = {
		selectedFilterValue: this.props.selectedFilterValue || ActionItemsFilterValue.CurrentUser,
	};

	public static getDerivedStateFromProps(props: IProps) {
		const { selectedFilterValue } = props;
		return selectedFilterValue ? { selectedFilterValue } : {};
	}

	private readonly triggerLabel: Record<ActionItemsFilterValue, string> = {
		[ActionItemsFilterValue.All]: 'Show all',
		[ActionItemsFilterValue.CurrentUser]: 'Show my action items',
		[ActionItemsFilterValue.OwnedBy]: 'Show action items assigned to...',
	};

	public render() {
		const { selectedFilterValue, selectedEmployee, showSelectEmployeeModal } = this.state;

		return (
			<>
				<Select value={selectedFilterValue} onValueChange={this.onSelectedFilterValueChanged}>
					<SelectTrigger styles={styleSheet.actionItemsFilter}>
						{selectedFilterValue === ActionItemsFilterValue.OwnedBy ? (
							<span>Assigned to {getDisplayName(selectedEmployee) || '...'}</span>
						) : (
							<span>{this.triggerLabel[selectedFilterValue ?? ActionItemsFilterValue.CurrentUser]}</span>
						)}
					</SelectTrigger>
					<SelectContent>
						<SelectItem value={ActionItemsFilterValue.All}>Show all</SelectItem>
						<SelectItem value={ActionItemsFilterValue.CurrentUser}>Show my action items</SelectItem>
						<SelectItem value={ActionItemsFilterValue.OwnedBy}>Show action items assigned to...</SelectItem>
					</SelectContent>
				</Select>
				<UserSelectModal
					bodyText='Filter action items by those owned by an employee.'
					className={`${css(styleSheet.selectAnEmployeeModal)}`}
					modalProps={{
						isOpen: showSelectEmployeeModal,
						onRequestClose: this.onSelectAnEmployeeClose,
						shouldCloseOnOverlayClick: true,
					}}
					title='Select an Employee'
				/>
			</>
		);
	}

	private onSelectAnEmployeeClose = (user?: UserViewModel, cancelled?: boolean) => {
		const { onFilterValueChanged } = this.props;
		const nextState: IState = { showSelectEmployeeModal: false };

		if (cancelled) {
			nextState.selectedEmployee = null;
			nextState.selectedFilterValue = ActionItemsFilterValue.CurrentUser;

			onFilterValueChanged?.(ActionItemsFilterValue.CurrentUser, null);
		} else {
			nextState.selectedEmployee = user;
			onFilterValueChanged?.(ActionItemsFilterValue.OwnedBy, nextState.selectedEmployee);
		}

		this.setState(nextState);
	};

	private onSelectedFilterValueChanged = (value: string) => {
		const dataContext = value as ActionItemsFilterValue;
		const showSelectEmployeeModal = dataContext === ActionItemsFilterValue.OwnedBy;

		this.setState({
			selectedEmployee: dataContext === ActionItemsFilterValue.OwnedBy ? this.state.selectedEmployee : null,
			selectedFilterValue: dataContext,
			showSelectEmployeeModal,
		});

		if (!showSelectEmployeeModal) {
			this.props.onFilterValueChanged?.(dataContext);
		}
	};
}
